import React, { useState, useEffect } from 'react';
import { X, Save, Settings, Store } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import OciParameterEditor from './OciParameterEditor';

const ShopEditor = ({ shop, onSave, onClose, user }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    supplier: '',
    baseUrl: '',
    description: '',
    category: '',
    username: '',
    password: '',
    isActive: true
  });
  const [showParameterEditor, setShowParameterEditor] = useState(false);

  useEffect(() => {
    if (shop) {
      setFormData({
        name: shop.name || '',
        supplier: shop.supplier || '',
        baseUrl: shop.base_url || '',
        description: shop.description || '',
        category: shop.category || '',
        // Nur bei neuen Shops oder wenn echte Credentials vorhanden sind
        username: (shop.id && shop.username === '***masked***') ? '' : (shop.username || ''),
        password: (shop.id && shop.password === '***masked***') ? '' : (shop.password || ''),
        isActive: shop.is_active !== undefined ? shop.is_active : true
      });
    }
  }, [shop]);

  if (!shop) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    // Shop-Basis-Daten (ID, etc.)
    const baseData = {
      id: shop.id,
      name: formData.name,
      supplier: formData.supplier,
      baseUrl: formData.baseUrl,
      description: formData.description,
      category: formData.category,
      isActive: formData.isActive
    };

    // Bei bestehenden Shops: Credentials nur hinzufügen wenn sie nicht leer sind
    if (shop.id) {
      // Nur hinzufügen wenn der Benutzer tatsächlich etwas eingegeben hat
      if (formData.username && formData.username.trim() !== '') {
        baseData.username = formData.username;
      }
      if (formData.password && formData.password.trim() !== '') {
        baseData.password = formData.password;
      }
    } else {
      // Bei neuen Shops: Immer alle Felder hinzufügen
      baseData.username = formData.username;
      baseData.password = formData.password;
    }

    console.log('🔍 Sending update data:', {
      ...baseData,
      username: baseData.username ? '***' : undefined,
      password: baseData.password ? '***' : undefined,
      usernameIncluded: 'username' in baseData,
      passwordIncluded: 'password' in baseData
    });

    onSave(baseData);
  };

  return (
    <div className={`fixed inset-0 ${isDarkMode ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/30'} flex items-center justify-center p-4 z-50`}>
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Modal Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.6)] backdrop-blur-2xl'
          : 'glass-light-card shadow-lg'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>


        <div className="relative flex flex-col h-full overflow-y-auto">
          {/* Header */}
          <div className="relative flex-shrink-0">
            {/* Header Background */}
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-r from-purple-900/20 via-indigo-900/20 to-purple-900/20 border-b border-white/10 backdrop-blur-xl'
              : 'bg-white border-b border-slate-200'
            } rounded-t-3xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-t from-white/5 to-white/15'
              : ''
            } rounded-t-3xl`}></div>

            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Store Icon */}
                  <div className="relative group/icon">
                    <div className={isDarkMode ? 'absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm' : 'hidden'}></div>
                    <div className={`relative ${isDarkMode
                      ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                      : 'bg-brand/10'
                    } w-12 h-12 rounded-2xl flex items-center justify-center`}>
                      <Store className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                      <div className={isDarkMode ? 'absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm' : 'hidden'}></div>
                    </div>
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${isDarkMode
                      ? 'bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                      : 'text-slate-900'
                    }`}>
                      {shop.id ? t('admin.shopEditor.editTitle') : t('admin.shopEditor.createTitle')}
                    </h3>
                    <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'} mt-1`}>{t('admin.shopEditor.subtitle')}</p>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className={`relative group/close overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                    isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 p-2'
                  }`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : undefined}
                >
                  {/* Hover Overlay - dark only */}
                  <div
                    className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/close:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                    }}
                  ></div>

                  <div className={`relative z-10 flex items-center justify-center transition-all duration-300 ${
                    isDarkMode ? 'text-white p-2 drop-shadow-lg group-hover/close:drop-shadow-xl' : 'text-slate-600'
                  }`}>
                    <X className="w-4 h-4 transition-transform duration-300 group-hover/close:scale-110" />
                  </div>

                  {/* Wave Effect - dark only */}
                  <div className={`absolute inset-0 opacity-0 group-hover/close:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                    <div
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/close:translate-x-full transition-transform duration-800"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                      }}
                    ></div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.name')}</label>
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                      : ''
                    } rounded-2xl`}></div>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                        ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                        : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                      }`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.supplier')}</label>
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                      : ''
                    } rounded-2xl`}></div>
                    <input
                      type="text"
                      value={formData.supplier}
                      onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                      className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                        ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                        : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                      }`}
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.url')}</label>
                <div className="relative">
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                    : ''
                  } rounded-2xl`}></div>
                  <input
                    type="url"
                    value={formData.baseUrl}
                    onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                    className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                      ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                      : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                    }`}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.description')}</label>
                <div className="relative">
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                    : ''
                  } rounded-2xl`}></div>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 resize-none ${isDarkMode
                      ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                      : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                    }`}
                    rows="3"
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.category')}</label>
                <div className="relative">
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                    : ''
                  } rounded-2xl`}></div>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                      ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                      : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                    }`}
                    placeholder={t('admin.shopEditor.categoryPlaceholder')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.username')}</label>
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                      : ''
                    } rounded-2xl`}></div>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                        ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                        : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                      }`}
                      placeholder={shop.id && shop.username === '***masked***' ? t('admin.shopEditor.credentialsUnchanged') : "shop-login"}
                    />
                  </div>
                  {shop.id && shop.username === '***masked***' && (
                    <p className={`text-xs ${isDarkMode ? 'text-blue-200 drop-shadow-sm' : 'text-brand'} mt-1`}>
                      {t('admin.shopEditor.credentialsHint')}
                    </p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.shopEditor.password')}</label>
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                      : ''
                    } rounded-2xl`}></div>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className={`relative w-full px-3 py-2 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                        ? 'bg-transparent border-0 focus:ring-2 focus:ring-purple-400/50 text-white placeholder-white/50'
                        : 'bg-white border border-slate-200 focus:ring-2 focus:ring-brand/40 text-slate-800 placeholder-slate-400'
                      }`}
                      placeholder={shop.id && shop.password === '***masked***' ? t('admin.shopEditor.credentialsUnchanged') : t('admin.shopEditor.password')}
                    />
                  </div>
                  {shop.id && shop.password === '***masked***' && (
                    <p className={`text-xs ${isDarkMode ? 'text-blue-200 drop-shadow-sm' : 'text-brand'} mt-1`}>
                      {t('admin.shopEditor.credentialsHint')}
                    </p>
                  )}
                </div>
              </div>

              {/* OCI-Parameter konfigurieren (nur für bestehende Shops) */}
              {shop.id && (
                <div className="relative group/params">
                  {/* Panel Background */}
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-br from-blue-600/20 via-indigo-600/20 to-blue-700/20 border border-blue-400/30 backdrop-blur-xl'
                    : 'bg-brand/5 border border-brand/20'
                  } rounded-2xl`}></div>
                  <div className={`absolute inset-0 ${isDarkMode
                    ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                    : ''
                  } rounded-2xl`}></div>

                  <div className="relative p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`text-lg font-medium ${isDarkMode ? 'text-white drop-shadow-sm' : 'text-slate-900'}`}>{t('admin.shopEditor.urlParams')}</h4>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-200 drop-shadow-sm' : 'text-brand'} mt-1`}>
                          {t('admin.shopEditor.urlParamsHint')}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowParameterEditor(true)}
                        className={`relative group/settings overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                          isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-4 py-2 flex items-center font-medium shadow-sm'
                        }`}
                        style={isDarkMode ? {
                          background: 'linear-gradient(135deg, rgba(147,51,234,0.7) 0%, rgba(126,34,206,0.7) 50%, rgba(107,33,168,0.7) 100%)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 8px 32px rgba(147,51,234,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                        } : undefined}
                      >
                        {/* Hover Overlay - dark only */}
                        <div
                          className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/settings:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                          style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                          }}
                        ></div>

                        <div className={`relative z-10 flex items-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'px-4 py-2 drop-shadow-lg group-hover/settings:drop-shadow-xl' : ''}`}>
                          <Settings className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/settings:scale-110" />
                          {t('admin.shopEditor.manageParams')}
                        </div>

                        {/* Wave Effect - dark only */}
                        <div className={`absolute inset-0 opacity-0 group-hover/settings:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                          <div
                            className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/settings:translate-x-full transition-transform duration-800"
                            style={{
                              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                            }}
                          ></div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className={`mr-2 rounded ${isDarkMode
                    ? 'border-white/30 bg-transparent text-purple-500 focus:ring-purple-400/50'
                    : 'border-slate-300 bg-white text-brand focus:ring-brand/40'
                  } focus:ring-offset-0`}
                />
                <label htmlFor="isActive" className={`text-sm ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'}`}>{t('admin.shopEditor.isActive')}</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                {/* Cancel Button */}
                <button
                  type="button"
                  onClick={onClose}
                  className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                    isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 font-medium'
                  }`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : undefined}
                >
                  {/* Hover Overlay - dark only */}
                  <div
                    className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                    }}
                  ></div>

                  <div className={`relative z-10 flex items-center justify-center font-medium transition-all duration-300 ${
                    isDarkMode ? 'text-white px-4 py-2 drop-shadow-lg group-hover/cancel:drop-shadow-xl' : ''
                  }`}>
                    {t('common.cancel')}
                  </div>

                  {/* Wave Effect - dark only */}
                  <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                    <div
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                      }}
                    ></div>
                  </div>
                </button>

                {/* Save Button */}
                <button
                  type="submit"
                  className={`relative group/save overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                    isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white px-4 py-2 flex items-center font-medium shadow-sm'
                  }`}
                  style={isDarkMode ? {
                    background: 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(21,128,61,0.7) 50%, rgba(22,101,52,0.7) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  } : undefined}
                >
                  {/* Hover Overlay - dark only */}
                  <div
                    className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/save:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                    style={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                    }}
                  ></div>

                  <div className={`relative z-10 flex items-center justify-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'px-4 py-2 drop-shadow-lg group-hover/save:drop-shadow-xl' : ''}`}>
                    <Save className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/save:scale-110" />
                    {t('common.save')}
                  </div>

                  {/* Wave Effect - dark only */}
                  <div className={`absolute inset-0 opacity-0 group-hover/save:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                    <div
                      className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/save:translate-x-full transition-transform duration-800"
                      style={{
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                      }}
                    ></div>
                  </div>
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Parameter Editor */}
        {showParameterEditor && (
          <OciParameterEditor
            shopId={shop.id}
            user={user}
            onClose={() => setShowParameterEditor(false)}
            onSave={() => {
              // Optional: Callback nach dem Speichern
              console.log('Parameter wurden gespeichert');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ShopEditor;
